import React, { useState, useEffect } from 'react';

const CODE_LINES = [
    [{ t: '// sprint #7 — auth module', c: 'text-slate-500' }],
    [],
    [{ t: 'const', c: 'text-purple-400' }, { t: ' handleLogin', c: 'text-white' }, { t: ' = async (', c: 'text-slate-400' }, { t: 'user', c: 'text-orange-300' }, { t: ') => {', c: 'text-slate-400' }],
    [{ t: '  await', c: 'text-[#11b36d]' }, { t: ' validate', c: 'text-blue-300' }, { t: '(user)', c: 'text-slate-400' }],
    [{ t: '  const', c: 'text-purple-400' }, { t: ' token', c: 'text-white' }, { t: ' = await', c: 'text-[#11b36d]' }, { t: ' jwt', c: 'text-blue-300' }, { t: '.sign(user)', c: 'text-slate-400' }],
    [{ t: '  return', c: 'text-[#11b36d]' }, { t: ' { token,', c: 'text-slate-400' }, { t: ' ok:', c: 'text-slate-400' }, { t: ' true', c: 'text-orange-300' }, { t: ' }', c: 'text-slate-400' }],
    [{ t: '}', c: 'text-slate-400' }],
];

const PIPELINE = ['Build', 'Test', 'Deploy'];

export default function HeroMockup({ scrollY = 0 }) {
    const [phase, setPhase] = useState(0);
    // phase 0: hidden  1: cards enter  2: code types  3: complete (becomes real)
    const [uptime, setUptime] = useState(0);

    useEffect(() => {
        const t1 = setTimeout(() => setPhase(1), 120);
        const t2 = setTimeout(() => setPhase(2), 500);
        const t3 = setTimeout(() => setPhase(3), 2000);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, []);

    const mainReady  = phase >= 1;
    const codeReady  = phase >= 2;
    const liveReady  = phase >= 3;

    useEffect(() => {
        if (!liveReady) return;
        const target = 99.9;
        const duration = 1400;
        const start = performance.now();
        let rafId;
        const tick = (now) => {
            const t = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setUptime(target * eased);
            if (t < 1) rafId = requestAnimationFrame(tick);
            else setUptime(target);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [liveReady]);

    return (
        <>
            <style>{`
                @keyframes heroFloat {
                    0%,100% { transform: rotateY(-14deg) rotateX(6deg) translateY(0px); }
                    50%     { transform: rotateY(-14deg) rotateX(6deg) translateY(-9px); }
                }
                @keyframes heroFloatSub {
                    0%,100% { transform: rotateY(-10deg) rotateX(4deg) translateY(0px); }
                    50%     { transform: rotateY(-10deg) rotateX(4deg) translateY(-6px); }
                }
                @keyframes heroFloatThird {
                    0%,100% { transform: rotateY(-8deg) rotateX(3deg) translateY(0px); }
                    50%     { transform: rotateY(-8deg) rotateX(3deg) translateY(-7px); }
                }
                @keyframes codeLine {
                    from { opacity:0; transform: translateX(-8px); }
                    to   { opacity:1; transform: translateX(0); }
                }
                @keyframes deployPop {
                    0%   { transform: scale(0.85); opacity:0; }
                    60%  { transform: scale(1.06); }
                    100% { transform: scale(1);    opacity:1; }
                }
                @keyframes greenPulseRing {
                    0%   { transform: scale(1);   opacity:0.7; }
                    100% { transform: scale(2.4); opacity:0; }
                }
                .hero-float       { animation: heroFloat      5s ease-in-out infinite; }
                .hero-float-sub   { animation: heroFloatSub   5s ease-in-out infinite 0.7s; }
                .hero-float-third { animation: heroFloatThird 5s ease-in-out infinite 1.3s; }
                .code-line        { opacity:0; }
                .code-line.visible { animation: codeLine 0.28s ease-out forwards; }
                .deploy-pop { animation: deployPop 0.4s cubic-bezier(0.16,1,0.3,1) forwards; }
                .pulse-ring {
                    position:absolute; inset:0; border-radius:9999px;
                    border: 1.5px solid #11b36d;
                    animation: greenPulseRing 1.4s ease-out infinite;
                }
            `}</style>

            <div className="relative w-full h-full flex items-center justify-center select-none"
                 style={{ perspective: '750px' }}>

                {/* ── MAIN CARD: code editor ── */}
                {/* Parallax wrapper */}
                <div style={{ transform: `translateY(${-scrollY * 0.05}px)`, transition: 'transform 0.05s linear' }}>
                    {/* Entrance wrapper — only handles translateX + opacity */}
                    <div
                        style={{
                            transform: `translateX(${mainReady ? 0 : 70}px)`,
                            opacity: mainReady ? 1 : 0,
                            transition: 'transform 0.75s cubic-bezier(0.16,1,0.3,1), opacity 0.55s ease',
                        }}
                    >
                    {/* Float + angle wrapper */}
                    <div className="hero-float w-[330px] sm:w-[360px] rounded-xl overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.4)] border border-white/[0.09]">
                        {/* Chrome */}
                        <div className="bg-[#1c1c1e] px-4 py-2.5 flex items-center gap-1.5 border-b border-white/[0.06]">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                            <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                            <span className="ml-3 flex-1 bg-white/[0.05] rounded px-2.5 py-0.5 text-[10px] text-slate-500 truncate">
                                auth/login.ts
                            </span>
                        </div>
                        {/* Code */}
                        <div className="bg-[#0e0e10] px-5 py-4 font-mono text-xs leading-6">
                            {CODE_LINES.map((tokens, i) => (
                                <div
                                    key={i}
                                    className={`code-line flex items-start ${codeReady ? 'visible' : ''}`}
                                    style={{ animationDelay: `${i * 0.11}s` }}
                                >
                                    <span className="w-5 text-right mr-4 text-slate-700 text-[10px] mt-px shrink-0">{i + 1}</span>
                                    <span>
                                        {tokens.length === 0
                                            ? <span>&nbsp;</span>
                                            : tokens.map((tok, j) => (
                                                <span key={j} className={tok.c}>{tok.t}</span>
                                            ))}
                                    </span>
                                </div>
                            ))}

                            {/* Status bar */}
                            <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {liveReady ? (
                                        <>
                                            <span className="relative flex items-center justify-center w-2 h-2">
                                                <span className="pulse-ring" />
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#11b36d]" />
                                            </span>
                                            <span className="text-[#11b36d] text-[10px]">Tests pasando · 24/24</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                                            <span className="text-yellow-400 text-[10px]">Ejecutando tests…</span>
                                        </>
                                    )}
                                </div>
                                <span className="text-slate-600 text-[10px]">Sprint #7</span>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/* ── SUB CARD: deploy pipeline ── */}
                {/* Parallax wrapper (fastest — closest to viewer) */}
                <div
                    className="absolute bottom-6 right-2 sm:right-0"
                    style={{ transform: `translateY(${-scrollY * 0.12}px)`, transition: 'transform 0.05s linear' }}
                >
                    {/* Entrance wrapper */}
                    <div
                        style={{
                            transform: `translateY(${mainReady ? 0 : 50}px)`,
                            opacity: mainReady ? 1 : 0,
                            transition: 'transform 0.75s cubic-bezier(0.16,1,0.3,1) 0.15s, opacity 0.55s ease 0.15s',
                        }}
                    >
                    <div className="hero-float-sub w-[154px] rounded-xl bg-[#111113] border border-white/[0.08] p-4 shadow-[0_12px_32px_rgba(0,0,0,0.45)]">
                        <p className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Deploy</p>

                        {liveReady ? (
                            <div className="deploy-pop">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="relative flex items-center justify-center w-2 h-2">
                                        <span className="pulse-ring" />
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#11b36d]" />
                                    </span>
                                    <span className="text-[11px] font-medium text-white">Production</span>
                                </div>
                                {PIPELINE.map((step) => (
                                    <div key={step} className="flex items-center justify-between mb-1.5">
                                        <span className="text-[10px] text-slate-400">{step}</span>
                                        <span className="text-[10px] text-[#11b36d]">✓</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse shrink-0" />
                                    <span className="text-[11px] font-medium text-white">Building…</span>
                                </div>
                                {PIPELINE.map((step, i) => (
                                    <div key={step} className="flex items-center justify-between mb-1.5">
                                        <span className="text-[10px] text-slate-400">{step}</span>
                                        <span className="text-[10px] text-slate-600">···</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-2 pt-2 border-t border-white/[0.05]">
                            <span className="text-[9px] text-slate-500">
                                {liveReady ? 'hace 2 minutos' : 'en progreso…'}
                            </span>
                        </div>
                    </div>
                    </div>
                </div>

                {/* ── THIRD CARD: uptime ── */}
                {/* Parallax wrapper (medium depth) */}
                <div
                    className="absolute top-4 right-1 sm:-right-2"
                    style={{ transform: `translateY(${-scrollY * 0.08}px)`, transition: 'transform 0.05s linear' }}
                >
                    {/* Entrance wrapper */}
                    <div
                        style={{
                            transform: `translateY(${mainReady ? 0 : -50}px)`,
                            opacity: mainReady ? 1 : 0,
                            transition: 'transform 0.75s cubic-bezier(0.16,1,0.3,1) 0.3s, opacity 0.55s ease 0.3s',
                        }}
                    >
                    <div className="hero-float-third w-[128px] rounded-xl bg-[#111113] border border-white/[0.08] p-3.5 shadow-[0_12px_32px_rgba(0,0,0,0.45)]">
                        <p className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Uptime</p>
                        <p className="text-2xl font-bold text-white leading-none tabular-nums">
                            {uptime.toFixed(1)}<span className="text-sm text-[#11b36d]">%</span>
                        </p>
                        <p className="text-[9px] text-slate-500 mt-1">últimos 30 días</p>
                        <div className="flex gap-0.5 mt-2">
                            {Array.from({ length: 18 }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`flex-1 rounded-sm transition-all duration-500 ${
                                        liveReady
                                            ? i === 11 ? 'bg-yellow-500/60 h-4' : 'bg-[#11b36d]/60 h-4'
                                            : 'bg-slate-700/40 h-4'
                                    }`}
                                    style={{ transitionDelay: liveReady ? `${i * 40}ms` : '0ms' }}
                                />
                            ))}
                        </div>
                    </div>
                    </div>
                </div>

            </div>
        </>
    );
}
